<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Aulas de Reposição</h5>
      <BButton
        v-if="hasPermission('adicionar_aulas_reposicao')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill mr-2" title="Nova Aula"></i>
      </BButton>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <select v-model="filters.quando" class="form-control" @change="filtrarAulas">
        <option value="Todos">Todos</option>
        <option value="Hoje">Hoje</option>
        <option value="Próximos">Próximos</option>
        <option value="Anteriores">Anteriores</option>
      </select>
      <input
        v-model="filters.aluno"
        placeholder="Filtrar por Aluno"
        class="form-control"
        @input="filtrarAulas"
      />
      <input
        v-model="filters.turma"
        placeholder="Filtrar por Turma"
        class="form-control"
        @input="filtrarAulas"
      />
      <select v-model="filters.status"
              class="form-select form-select-sm"
              style="max-width: 180px;"
              @change="filtrarAulas">
        <option value="">Todos os status</option>
        <option value="Agendada">Agendada</option>
        <option value="Compareceu">Compareceu</option>
        <option value="Faltou">Faltou</option>
      </select>
      <BButton variant="secondary" @click="limparFiltros">
        <i class="bi bi-funnel mr-2" title="Limpar Filtros"></i>
      </BButton>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="sort('id')">
              # {{ arrow('id') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('aluno_nome')">
              Aluno {{ arrow('aluno_nome') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('data_requisicao')">
              Data Requisição {{ arrow('data_requisicao') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('data_aula')">
              Data Aula {{ arrow('data_aula') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('turma_completa')">
              Turma {{ arrow('turma_completa') }}
            </button>
          </th>

          <th class="text-center">Status</th>
          <th class="text-center">Ações</th>

        </tr>
      </thead>
      <tbody>
        <tr v-for="row in aulasReposicao" :key="row.id">
          <td>{{ row.id }}</td>
          <td>{{ row.aluno_nome }}</td>
          <td>{{ formatDate(row.data_requisicao) }}</td>
          <td>{{ formatDate(row.data_aula) }}</td>
          <td>{{ row.turma_completa }}</td>
          <td class="text-center">
            <span
              :class="{
                'text-primary': row.status === 'Agendada',
                'text-success': row.status === 'Compareceu',
                'text-danger': row.status === 'Faltou'
              }"
            >
              {{ row.status }}
            </span>
          </td>
          <td class="text-end">
            <i
              v-if="hasPermission('detalhar_aulas_reposicao')"
              class="bi bi-eye-fill text-primary mx-1"
              title="Detalhar"
              @click="detalharAula(row.id)"
            ></i>
            <i
              v-if="hasPermission('reagendar_aulas_reposicao') && row.status != 'Compareceu'"
              class="bi bi-calendar-event text-info mx-1 cursor-pointer"
              title="Reagendar aula"
              role="button" tabindex="0"
              @click="abrirReagendar(row)"
              @keydown.enter="abrirReagendar(row)"
            ></i>
            <i
              v-if="hasPermission('alterar_aulas_reposicao')"
              class="bi bi-pencil-fill text-warning mx-1"
              title="Editar"
              @click="editarAula(row.id)"
            ></i>
            <i
              v-if="hasPermission('excluir_aulas_reposicao')"
              class="bi bi-trash-fill text-danger mx-1"
              title="Excluir"
              @click="excluirAula(row.id)"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <!-- 1. Resumo de registros -->
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <!-- 2. Seletor de itens por página -->
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limitePorPagina"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @update:model-value="onChangeLimit"
        />
      </div>

      <!-- 3. Controle de página -->
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="paginaAtual"
          :per-page="limitePorPagina"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @update:model-value="onPageChange"
        />
      </div>
    </div>
  </div>
  <ReagendarAulaReposicao
    :show="modalReagendarAberto"
    :aula="aulaSelecionada"
    @close="fecharReagendar"
    @saved="() => proxy?.$toast?.success?.('Aula reagendada com sucesso')"
  />
</template>

<script setup>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import { usePermissions } from '@/composables/usePermissions'
import globalUtils from '@/utils/globalUtils'
import ReagendarAulaReposicao from '@/components/modals/ReagendarAulaReposicao.vue'

const router = useRouter()
const { proxy } = getCurrentInstance()
const { fetchPermissions, hasPermission } = usePermissions()

// estados reativos
const aulasReposicao = ref([])
const filters = reactive({ quando: 'Todos', aluno: '', turma: '', status: 'Agendada' })
const paginaAtual = ref(1)
const limitePorPagina = ref(10)
const totalPages = ref(1)
const totalItems = ref(0)
const currentSort = reactive({ key: 'aluno_nome', order: 'asc' })

const modalReagendarAberto = ref(false)
const aulaSelecionada = ref(null)

// opções de limite de página
const pageOptions = ref([10, 15, 20, 25, 50])

// cálculo do intervalo
const startRow = computed(() =>
  (paginaAtual.value - 1) * limitePorPagina.value + 1
)
const endRow = computed(() =>
  Math.min(paginaAtual.value * limitePorPagina.value, totalItems.value)
)

function abrirReagendar(aula) {
  aulaSelecionada.value = { ...aula }
  modalReagendarAberto.value = true
}

function fecharReagendar(payload) {
  modalReagendarAberto.value = false
  if (payload?.saved) carregarAulas()
}

// formatação de data utilitária
function formatDate(dt) {
  return globalUtils.formatarData(dt) || '—'
}

// carregar dados
async function carregarAulas() {
  try {
    const params = {
      page: paginaAtual.value,
      limit: limitePorPagina.value,
      quando: filters.quando !== 'Todos' ? filters.quando : undefined,
      aluno: filters.aluno || undefined,
      turma: filters.turma || undefined,
      status: filters.status || undefined,
      sortField: currentSort.key,
      sortOrder: currentSort.order
    }
    const res = await axios.get(
      `/operacional/aulas-reposicao`,
      { params }
    )
    aulasReposicao.value = res.data.data
    totalPages.value = res.data.totalPages
    totalItems.value   = res.data.totalItems 
                        ?? res.data.total 
                        ?? res.data.data.length

  } catch (e) {
    console.error('Erro ao carregar aulas de reposição:', e)
  }
}

// ordenação
function sort(col) {
  if (currentSort.key === col) {
    currentSort.order = currentSort.order === 'asc' ? 'desc' : 'asc'
  } else {
    currentSort.key = col
    currentSort.order = 'asc'
  }
  carregarAulas()
}
function arrow(col) {
  if (currentSort.key !== col) return ''
  return currentSort.order === 'asc' ? '▲' : '▼'
}

// filtros
function filtrarAulas() {
  paginaAtual.value = 1
  carregarAulas()
}
function limparFiltros() {
  filters.quando = 'Todos'
  filters.aluno = ''
  filters.turma = ''
  filters.status = ''
  paginaAtual.value = 1
  carregarAulas()
}

// paginação
function onPageChange(page) {
  paginaAtual.value = page
  carregarAulas()
}

function onChangeLimit(limit) {
  limitePorPagina.value = Number(limit)
  paginaAtual.value = 1
  carregarAulas()
}

// navegação e CRUD
function irParaCadastro() { router.push('/operacional/aulas-reposicao/novo') }
function detalharAula(id) { router.push(`/operacional/aulas-reposicao/${id}/detalhar`) }
function editarAula(id) { router.push(`/operacional/aulas-reposicao/${id}`) }
async function excluirAula(id) {
  const ok = proxy
    ? await proxy.$confirm('Confirmar exclusão?', 'Confirmação')
    : confirm('Confirmar exclusão?')
  if (!ok) return
  try {
    await axios.delete(
      `/operacional/aulas-reposicao/${id}`
    )
    proxy?.$toast?.success('Excluído com sucesso')
    carregarAulas()
  } catch (e) {
    console.error('Erro ao excluir aula:', e)
    proxy?.$alert('Não foi possível excluir.', 'Erro')
  }
}

// inicialização
onMounted(async () => {
  await fetchPermissions()
  carregarAulas()
})
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}

</style>